import { faThumbtack } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box } from '@mui/system';
import { useMemo, useState } from 'react';
import {
  Change,
  ChangeType,
  ContactChangeType,
  HistoryEntity,
  RoomChangeType,
  TemplateChangeType,
  ToolChangeType,
} from '../../../modules/dashboard/models/dashboard.types';
import {
  faPaperPlane,
  faPeopleRoof,
  faScrewdriverWrench,
} from '@fortawesome/pro-thin-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Tooltip } from '../../../shared/ui/Tooltip/Tooltip';
import styles from './ChangesPage.module.scss';
import { useNewsCenter } from '../../../modules/newscenter/hooks/use-news-center.hook';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Menu } from '../../../shared/ui/Menu/Menu';
import { MessageAction } from '../../../modules/newscenter/models/message.types';
import { MenuItem } from '../../../shared/models/shared.types';
import pg_icon from '../../../assets/pictures/pg_profile.png';
import ChangeContent from './ChangeContent';
import { useDashboard } from '../hooks/use-dashboard.hook';

export type FavoriteHistoryItemProps = {
  change: Change;
  onToggleFavorite: () => void;
};

export const FavoriteHistoryItem = ({
  change,
  onToggleFavorite,
}: FavoriteHistoryItemProps) => {
  const { getChangeType } = useDashboard();

  const dateKey = change.create_change_info.created_at.slice(0, 10);
  const dateObj = new Date(dateKey);
  const formattedDate = dateObj.toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const changeType = getChangeType(change)?.change_type;
  const changeTypeDetail = getChangeType(change)?.change_type_detail;
  const senderName =
    change.create_change_info.created_by.profile?.personal_data.first_name ||
    'Avatar';
  const senderAvatar = getChangeType(change)?.sender_avatar || pg_icon;
  const pengueenAvatar =
    'https://files.pengueen.de/api/download/6b0dfc56-6d62-4376-aff4-ae380f5487d5.png';
  const imgClassName =
    'w-8 h-8 rounded-full border border-solid border-gray-200';

  const icon = useMemo(() => {
    switch (changeType) {
      case ChangeType.Contact:
        return <FontAwesomeIcon icon={faPeopleRoof as IconProp} />;
      case ChangeType.Room:
        return <FontAwesomeIcon icon={faPaperPlane as IconProp} />;
      case ChangeType.Template:
        return <FontAwesomeIcon icon={faPeopleRoof as IconProp} />;
      case ChangeType.Tool:
        return <FontAwesomeIcon icon={faScrewdriverWrench as IconProp} />;
      default:
        return <FontAwesomeIcon icon={faPeopleRoof as IconProp} />;
    }
  }, [changeType]);

  return (
    <Box
      className="flex flex-col hover:underline shadow-md rounded-md p-4 pb-6 mt-1 mb-2 gap-3"
      sx={{ backgroundColor: '#A3D3F7' }}
    >
      <div className="flex flex-row w-full">
        <div className="flex flex-1 gap-1 items-center font-semibold w-36 shrink-0">
          <div className="text-xl pr-1">{icon}</div>
          <img
            src={change.community?.logo?.url ?? pengueenAvatar}
            className={imgClassName}
            title={change.community?.name ?? 'community name'}
            alt={change.community?.name ?? 'community name'}
          />
          <img
            src={senderAvatar}
            className={imgClassName}
            title={senderName}
            alt={senderName}
          />
        </div>
        <div className="flex flex-row gap-2">
          <span className="text-xs">{formattedDate}</span>
          <Box
            onClick={onToggleFavorite}
            className="cursor-pointer"
            sx={{ color: change.favorite ? 'error.main' : 'text.secondary' }}
          >
            <FontAwesomeIcon icon={faThumbtack} />
          </Box>
        </div>
      </div>
      <div className="flex flex-row items-center text-sm font-semibold">
        <ChangeContent change={change} changeTypeDetail={changeTypeDetail} />
      </div>
    </Box>
  );
};
